import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export const CustomAlertModal = ({
  visible,
  onClose,
  text,
  onDonePress,
  onCancelPress,
  doneText,
  cancelText,
  onSingleButton = true,
  isError = false,
}) => {
  const { t } = useTranslation();
  const resolvedDoneText = doneText ?? t("done") ?? "Done";

  if (!visible) return null;

  const handleDone = () => {
    onDonePress?.();
    onClose?.();
  };

  const handleCancel = () => {
    onCancelPress?.();
    onClose?.();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-xs"
        />

        {/* Dialog */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: "spring", damping: 25, stiffness: 350 }}
          className="relative bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full z-10 flex flex-col items-center text-center border border-gray-100"
        >
          {/* Icon */}
          <div
            className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${
              isError
                ? "bg-red-50 text-red-500"
                : "bg-amber-50 text-amber-500"
            }`}
          >
            <AlertCircle className="w-6 h-6 stroke-[2.2]" />
          </div>

          {/* Message Text (preserves newlines if error contains list) */}
          <div className="text-sm font-semibold text-gray-800 whitespace-pre-line mb-6 max-h-60 overflow-y-auto w-full px-1">
            {text}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 w-full">
            {!onSingleButton && (
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                {cancelText ?? t("cancel") ?? "Cancel"}
              </button>
            )}
            <button
              type="button"
              onClick={handleDone}
              className="flex-1 py-3 px-4 rounded-xl bg-[#0B0E39] hover:bg-[#1E2260] text-white text-sm font-bold shadow-md transition-all"
            >
              {resolvedDoneText}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
